import {
  combineReducers,
  configureStore,
  type Middleware,
  type Reducer,
} from '@reduxjs/toolkit';
import type { AnyAction, PreloadedState, Store } from 'redux';

import { changeDiscoverAppStatusApi } from '../features/DisableCMDBDiscoveredAppsModal/data-store/changeDiscoverAppStatusSlice';
import { discoveryApplicationApi } from '../features/DiscoverCMDBApps/data-layer/ApplicationSlice';
import discoveryExistingConnectionSlice from '../features/DiscoverCMDBApps/data-layer/DiscoveryExistingConnectionSlice';
import discoverySaveConnectionSlice from '../features/DiscoverCMDBApps/data-layer/DiscoverySaveConnectionSlice';
import { discoveryFileUploadApi } from '../features/DiscoveryFileUpload/data-store/DiscoveryFileUploadApi';
import discoveryFileUploadSlice from '../features/DiscoveryFileUpload/data-store/DiscoveryFileUploadSlice';
import { startImportApi } from '../features/DiscoveryImport/data-store/startImportSlice';
import { integrationAllTabApi } from '../features/Integrations/data-store/IntegrationAllTabApi';
import integrationSearchToggelFilterSlice from '../features/Integrations/data-store/integrationSearchFilterSlice';
import { discoveryConfigByIdApi } from '../features/ManageCMDBDiscoveredApps/data-store/getDiscoveryConfigByIdSlice';
import { saveDiscoveryConfigApi } from '../features/ManageCMDBDiscoveredApps/data-store/saveDiscoveryConfigSlice';
import Logger from '../services/loggerService';

import { globalSlice } from './globalSlice';
import { obNextGenApi } from './obNextGenSlice';

// --------------------------------------------
// Utility: Deep clone for safety
// --------------------------------------------

/**
 * Performs a deep clone using JSON serialization.
 * This ensures immutability when rehydrating state.
 *
 * @param obj - Any serializable object to be deeply cloned.
 * @returns A deep-cloned copy of the original object.
 */
const deepClone = <T>(obj: T): T => JSON.parse(JSON.stringify(obj)) as T;

// --------------------------------------------
// Bootstrap Parent Store
// --------------------------------------------

/**
 * Generic representation of Redux state as a key-value structure.
 */
type GenericState = Record<string, unknown>;

/**
 * Typed version of the browser window that optionally includes the Redux parent store.
 */
type TypedWindow = typeof window & {
  store?: Store<GenericState>;
};

// Get the typed global window object.
const typedWindow = window as TypedWindow;

// Extract parent store from global scope (shared between apps or micro-frontends).
const parentStore = typedWindow.store;

// Handle missing parent store gracefully.
if (!parentStore) {
  Logger.error('Parent Redux store is not available on window');
  throw new Error('Parent Redux store not found');
}

// Retrieve the current state from the parent store.
const rawParentState = parentStore.getState();

// --------------------------------------------
// Reducer Setup
// --------------------------------------------

/**
 * List of known RTK Query API slices to be integrated in the store.
 */
const apiSlices = [
  obNextGenApi,
  discoveryConfigByIdApi,
  discoveryApplicationApi,
  globalSlice,
  discoveryFileUploadApi,
  integrationAllTabApi,
  startImportApi,
  changeDiscoverAppStatusApi,
  saveDiscoveryConfigApi,
];

const staticReducers = {
  discoverySaveConnection: discoverySaveConnectionSlice.reducer,
  discoveryExistingConnection: discoveryExistingConnectionSlice.reducer,
  discoveryFileUpload: discoveryFileUploadSlice.reducer,
  discoveryAllTabSearchToggelFilter: integrationSearchToggelFilterSlice.reducer,
};

/**
 * Dynamically collect reducers from the provided API slices.
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
const dynamicReducers: Record<string, Reducer> = {
  ...staticReducers,
  ...Object.fromEntries(apiSlices.map((api) => [api.reducerPath, api.reducer])),
};

/**
 * Creates a passthrough reducer for non-API slices that exist in parent state.
 * These reducers just return the current state as-is.
 *
 * @param initialState - The initial state from the parent store.
 * @returns A passthrough reducer function.
 */
function createPassthroughReducer<T>(initialState: T): Reducer<T> {
  return (state: T = initialState): T => state;
}

/**
 * Loop over each key in the parent state, and if it isn't handled by a known API reducer,
 * create a passthrough reducer to maintain the state.
 */
const keysToPassThrough = ['user'];

keysToPassThrough.forEach((key) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!dynamicReducers[key] && rawParentState[key] !== undefined) {
    dynamicReducers[key] = createPassthroughReducer(rawParentState[key]);
  }
});

// Combine all collected and passthrough reducers.
const rootReducer = combineReducers(dynamicReducers);

/**
 * RootState inferred from the combined reducer, used for store typing.
 */
type RootState = ReturnType<typeof rootReducer>;

/**
 * Typed version of the parent store with the inferred RootState.
 */
const typedParentStore = parentStore as Store<RootState>;

// --------------------------------------------
// Store Creation
// --------------------------------------------

/**
 * Create the child Redux store using:
 * - Root reducer
 * - Preloaded state (deep cloned from parent)
 * - Middleware including API middleware
 */
export const store = configureStore({
  reducer: rootReducer,
  preloadedState: {
    user: deepClone(rawParentState.user),
  } as PreloadedState<RootState>,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false })
      .concat(obNextGenApi.middleware as Middleware<object, RootState>)
      .concat(
        discoveryConfigByIdApi.middleware as Middleware<object, RootState>
      )
      .concat(
        discoveryApplicationApi.middleware as Middleware<object, RootState>
      )
      .concat(
        discoveryFileUploadApi.middleware as Middleware<object, RootState>
      )
      .concat(integrationAllTabApi.middleware as Middleware<object, RootState>)
      .concat(startImportApi.middleware as Middleware<object, RootState>)
      .concat(
        changeDiscoverAppStatusApi.middleware as Middleware<object, RootState>
      )
      .concat(
        saveDiscoveryConfigApi.middleware as Middleware<object, RootState>
      ),
});

// --------------------------------------------
// SYNC: Keep child store in sync with parent store
// --------------------------------------------

/**
 * Subscribe to parent store changes.
 * If any slice of state differs from the child store, dispatch a custom SYNC action to update it.
 */
typedParentStore.subscribe(() => {
  const nextState = typedParentStore.getState();

  const syncKeys: (keyof RootState)[] = ['user']; // 👈 Constrain keys

  syncKeys.forEach((key) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const nextValue = nextState[key];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const currentValue = store.getState()[key];

    if (!Object.is(currentValue, nextValue)) {
      store.dispatch({
        type: `SYNC_PARENT_STATE/${String(key)}`,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        payload: deepClone(nextValue),
      });
    }
  });
});

// --------------------------------------------
// Extend Reducers to Handle SYNC_PARENT_STATE
// --------------------------------------------

/**
 * Wrap each reducer to handle SYNC_PARENT_STATE action for its slice.
 * When such an action is received, it replaces the slice with the payload.
 */
type SliceKey = keyof RootState;
type SliceState<K extends SliceKey> = RootState[K];

Object.keys(dynamicReducers).forEach((key) => {
  const baseReducer = dynamicReducers[key] as Reducer<
    SliceState<typeof key>,
    AnyAction
  >;

  const wrappedReducer: Reducer<SliceState<typeof key>, AnyAction> = (
    state,
    action
  ) => {
    if (action.type === `SYNC_PARENT_STATE/${key}`) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return action.payload;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return baseReducer(state, action);
  };

  dynamicReducers[key] = wrappedReducer;
});

// Replace the store's reducer with the updated one that supports SYNC actions.
store.replaceReducer(combineReducers(dynamicReducers));

// --------------------------------------------
// Exports
// --------------------------------------------

/**
 * Type of the store's dispatch function.
 */
export type AppDispatch = typeof store.dispatch;

/**
 * Export RootState type for global usage in selectors, hooks, etc.
 */
export type { RootState };
